import { defaultError } from '@qpr/client-library';
import api, { ApiResult } from '../../../api/api-caller';
import initialState, { Filter, FilterState } from '../filter-state';

const errorHandler = (apiResult: ApiResult): FilterState => {
  return {
    ...initialState,
    error: apiResult.error || defaultError
  };
};

const extractDataFromResponse = (result: ApiResult) => {
  const { data } = result;

  return data;
};

const fetchFilters = async (): Promise<ApiResult> => {
  const call = {
    method: 'GET'
  };

  const result: ApiResult = await api.asyncCallApi('filters', call);

  return result;
};

const deleteFilterFromAPI = async (filterToDelete: Filter): Promise<ApiResult> => {
  const call = {
    method: 'DELETE'
  };

  const result: ApiResult = await api.asyncCallApi(`filters/${filterToDelete._id}`, call);
  return result;
};

const deleteFilter = async (filterToDelete: Filter): Promise<FilterState> => {
  try {
    const result = await deleteFilterFromAPI(filterToDelete);

    if (!result.ok) {
      return errorHandler(result);
    }

    const filter: Filter = result.data;

    return { ...initialState, data: [filter] };
  } catch (error) {
    return {
      ...initialState,
      error: { message: 'Server unreachable', details: JSON.stringify(error) }
    };
  }
};

const postOrPutFilterToApi = async (filterToSave: Filter): Promise<ApiResult> => {
  if (filterToSave._id === '') {
    const call = {
      method: 'POST',
      body: {
        name: filterToSave.name,
        isPublic: filterToSave.isPublic,
        rule: filterToSave.rule
      }
    };

    const result: ApiResult = await api.asyncCallApi('filters', call);
    return result;
  }
  const call = {
    method: 'PUT',
    body: {
      name: filterToSave.name,
      isPublic: filterToSave.isPublic,
      rule: filterToSave.rule
    }
  };

  const result: ApiResult = await api.asyncCallApi(`filters/${filterToSave._id}`, call);
  return result;
};

const saveFilter = async (filterToSave: Filter): Promise<FilterState> => {
  try {
    const result = await postOrPutFilterToApi(filterToSave);

    if (!result.ok) {
      return errorHandler(result);
    }

    const filter: Filter = result.data;

    return { ...initialState, data: [filter] };
  } catch (error) {
    return {
      ...initialState,
      error: { message: 'Server unreachable', details: JSON.stringify(error) }
    };
  }
};

const doGetData = async (): Promise<FilterState> => {
  try {
    const result = await fetchFilters();

    if (!result.ok) {
      return errorHandler(result);
    }

    const data: Filter[] = extractDataFromResponse(result).map((filter: Filter) => {
      filter.rule = filter.rule.replace(/&lt;/g, '<');
      filter.rule = filter.rule.replace(/&gt;/g, '>');
      return filter;
    });

    return { ...initialState, data };
  } catch (error) {
    return {
      ...initialState,
      error: { message: 'Server unreachable', details: JSON.stringify(error) }
    };
  }
};

// allow use string to access element
interface IData {
  [key: string]: any;
}
const viewModelCache: IData = {};

export interface FilterViewModelType {
  fetchData(): Promise<FilterState>;
  getData(): Promise<FilterState>;
  deleteFilter(filterToDelete: Filter): Promise<FilterState>;
  saveFilter(filterToSave: Filter): Promise<FilterState>;
}

const createFilterViewModel = (): FilterViewModelType => {
  let data: FilterState;
  const cacheKey: string = '';

  const fetchData = async (): Promise<FilterState> => {
    if (viewModelCache[cacheKey]) {
      data = viewModelCache[cacheKey];
    } else {
      data = await doGetData();
      viewModelCache[cacheKey] = data;
    }
    return data;
  };

  const getData = async (): Promise<FilterState> => {
    if (data) {
      return data;
    }
    await fetchData();
    return data;
  };

  return {
    fetchData,
    getData,
    deleteFilter,
    saveFilter
  };
};

export default createFilterViewModel;
